/**
 * FBC top-nav helpers for cycle breadcrumb / switcher labels.
 */

import {
  FC_MONTH_ABBREV,
  FC_ROUTES,
  FBC_OTHER_CYCLE_PREFIX_PATTERN,
  FBC_PAGE_FIRST_SLUGS,
  MONGO_OBJECT_ID_PATTERN,
} from '@/modules/financialclose/utils/constants';

export function formatCycleDate(iso, { fullDate = true } = {}) {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const month = FC_MONTH_ABBREV[date.getMonth()];
  const year = date.getFullYear();
  if (!fullDate) return `${month} ${year}`;
  const day = date.getDate();
  return `${day} ${month} ${year}`;
}

export function getCycleEndDate(cycle) {
  return cycle?.to || cycle?.enddate || cycle?.metadata?.enddate || cycle?.period?.end || null;
}

export function getCycleStartDate(cycle) {
  return cycle?.from || cycle?.startdate || cycle?.metadata?.startdate || cycle?.period?.start || null;
}

export function getCycleDescription(cycle) {
  return cycle?.description || cycle?.metadata?.description || cycle?.name || '';
}

/**
 * Breadcrumb label: description + date range when dates exist.
 * Example: "Financial Close For 1 Mar 2026 To 31 Mar 2026"
 * If description already contains the range, return description as-is.
 */
export function getCycleBreadcrumbLabel(cycle) {
  if (!cycle) return '';
  const description = getCycleDescription(cycle);
  const from = formatCycleDate(getCycleStartDate(cycle));
  const to = formatCycleDate(getCycleEndDate(cycle));

  if (description && from && to) {
    const rangeHint = `${from} to ${to}`.toLowerCase();
    if (description.toLowerCase().includes(rangeHint.replace(/\s+/g, ' '))) {
      return description;
    }
    if (/\d{1,2}\s+[A-Za-z]{3}\s+\d{4}/.test(description)) {
      return description;
    }
    return `${description} (${from} to ${to})`;
  }

  if (description) return description;
  if (from && to) return `${from} to ${to}`;
  return description || '';
}

export function sortCyclesByEndDateDesc(cycles = []) {
  return [...cycles].sort((a, b) => {
    const timeA = new Date(getCycleEndDate(a)).getTime() || 0;
    const timeB = new Date(getCycleEndDate(b)).getTime() || 0;
    return timeB - timeA;
  });
}

export function extractCycleIdFromPath(path = '') {
  const value = String(path || '');
  // Setup Task Library :id is a template id, not a cycle/project id.
  if (value.includes('/financialclose/setup/tasklist/')) return null;

  const pageSlugs = FBC_PAGE_FIRST_SLUGS.join('|');
  const pageFirst = value.match(
    new RegExp(`/financialclose/project/(?:${pageSlugs})/(${MONGO_OBJECT_ID_PATTERN})`, 'i'),
  );
  if (pageFirst) return pageFirst[1];

  // ID-first variance (and any other /project/:id/...) routes.
  const idFirst = value.match(
    new RegExp(`/financialclose/project/(${MONGO_OBJECT_ID_PATTERN})(?:/|$)`, 'i'),
  );
  if (idFirst) return idFirst[1];

  const otherCycle = value.match(
    new RegExp(
      `/financialclose/(?:${FBC_OTHER_CYCLE_PREFIX_PATTERN})/(?:[^/]+/)?(${MONGO_OBJECT_ID_PATTERN})`,
      'i',
    ),
  );
  if (otherCycle) return otherCycle[1];

  const nested = value.match(new RegExp(`/(${MONGO_OBJECT_ID_PATTERN})(?:/|$)`, 'i'));
  return nested ? nested[1] : null;
}

export function extractCycleIdFromRoute(route = {}) {
  const path = route?.path || '';
  // Setup Task Library detail uses :id for template id — not a cycle/project id.
  if (path.includes('/financialclose/setup/tasklist/')) return null;
  return route?.params?.id || route?.params?.projectId || extractCycleIdFromPath(path);
}

/**
 * Build the path to open after switching cycles from the breadcrumb dropdown.
 * Keeps the current FC page when possible; task-detail routes fall back to Tasklist.
 */
export function buildCycleSwitchPath(currentPath = '', newCycleId, currentCycleId = null) {
  if (!newCycleId) return null;

  // Task detail URLs are cycle-specific — land on Tasklist for the new cycle.
  if (/\/financialclose\/project\/checklist\/[^/]+\/task\//i.test(currentPath)) {
    return FC_ROUTES.CHECKLIST(newCycleId);
  }

  // TM analysis step URLs are cycle-specific — land on TM main for the new cycle.
  if (/\/financialclose\/automationtransactionmatching\/[^/]+\/c\//i.test(currentPath)) {
    return `/financialclose/automationtransactionmatching/${newCycleId}`;
  }

  if (currentCycleId && currentPath.includes(String(currentCycleId))) {
    return currentPath.split(String(currentCycleId)).join(String(newCycleId));
  }

  const replaced = currentPath.replace(
    new RegExp(`/(${MONGO_OBJECT_ID_PATTERN})(?=/|$)`, 'i'),
    `/${newCycleId}`,
  );
  if (replaced !== currentPath && replaced.includes(String(newCycleId))) {
    return replaced;
  }

  return FC_ROUTES.CHECKLIST(newCycleId);
}

export function isFbcCyclesLanding(path = '') {
  return path === '/financialclose/home' || path.startsWith('/financialclose/home?');
}

export function isFbcSettingsRoute(path = '') {
  // Setup pages intentionally have no left side nav (gear / top-nav only).
  // FS Preparation still lives under /manage/ but is treated as a cycle route.
  return path.includes('/financialclose/setup/');
}

export function isFbcCycleRoute(path = '') {
  if (!path.includes('/financialclose/')) return false;
  if (isFbcCyclesLanding(path)) return false;

  // FS Preparation lives under /manage/ but uses cycle nav, not setup masters
  if (
    path.includes(FC_ROUTES.MANAGE_FS_PREPARATION_PREFIX) ||
    path.includes(FC_ROUTES.FS_PREPARATION_PATH_INFIX)
  ) {
    return true;
  }

  if (isFbcSettingsRoute(path)) return false;

  return path.includes('/financialclose/project/') || path.includes('/financialclose/automation');
}

export function resolveFbcNavContext(path = '') {
  if (!path.includes('/financialclose')) return 'app';
  if (isFbcCyclesLanding(path)) return 'hidden';
  // Setup masters use gear / top-nav only — no left side nav.
  if (isFbcSettingsRoute(path)) return 'hidden';
  if (isFbcCycleRoute(path)) {
    return 'fbc-cycle';
  }
  return 'hidden';
}
